import { getTripsByIds } from '../api/vakanza-api.js'
import { getProgramByKey } from '../data/programs/index.js'

/*
 * Jedno wspólne źródło kompletnych danych programu.
 * Karty terminów, sticky pasek i katalog nie powinny samodzielnie powtarzać
 * pobierania, łączenia oraz sortowania danych.
 */

const CLIENT_CACHE_TTL_MS = 30_000
const programTermsCache = new Map()

/*
 * Tylko te informacje mogą być uzupełniane ręcznie dla konkretnego terminu.
 * Jawna lista chroni ceny, dostępność, daty i linki pochodzące z Vakanzy przed
 * przypadkowym nadpisaniem w lokalnym pliku programu.
 */
const MANUAL_TERM_DETAIL_KEYS = [
  'pilotId',
  'group',
  'airport',
  'priceNote',
  'priceNotePrice',
  'expandedDetails',
]

export async function getProgramTerms(
  programKey,
  { forceRefresh = false } = {}
) {
  const normalizedKey = String(programKey).trim()
  const cached = programTermsCache.get(normalizedKey)

  if (
    !forceRefresh &&
    cached &&
    cached.expiresAt > Date.now()
  ) {
    return cached.promise
  }

  const promise = loadProgramTerms(normalizedKey)

  programTermsCache.set(normalizedKey, {
    expiresAt: Date.now() + CLIENT_CACHE_TTL_MS,
    promise,
  })

  try {
    return await promise
  } catch (error) {
    const currentCache =
      programTermsCache.get(normalizedKey)

    if (currentCache?.promise === promise) {
      programTermsCache.delete(normalizedKey)
    }

    throw error
  }
}

export function clearProgramTermsCache(programKey) {
  if (programKey === undefined) {
    programTermsCache.clear()
    return
  }

  programTermsCache.delete(String(programKey).trim())
}

async function loadProgramTerms(programKey) {
  const program = getProgramByKey(programKey)

  if (!program) {
    return {
      program: null,
      terms: [],
      omittedTripIds: [],
      errors: [],
    }
  }

  const result = await getTripsByIds(program.tripIds)

  return mergeProgramTerms(program, result)
}

/* Czysta funkcja pozostaje łatwa do sprawdzenia bez wykonywania zapytań API. */
export function mergeProgramTerms(program, result) {
  const hiddenTripIds = result.data
    .filter(trip => trip.isHidden === true)
    .map(trip => trip.id)
  const omittedTripIds = [
    ...new Set([
      ...result.missing,
      ...hiddenTripIds,
    ]),
  ]
  const terms = result.data
    .filter(trip => trip.isHidden !== true)
    .map(trip => {
      const configuredDetails =
        program.termDetails?.[String(trip.id)] ?? {}
      const manualDetails = Object.fromEntries(
        MANUAL_TERM_DETAIL_KEYS
          .filter(key => key in configuredDetails)
          .map(key => [key, configuredDetails[key]])
      )

      return {
        ...trip,
        ...manualDetails,
      }
    })
    .sort((firstTrip, secondTrip) =>
      String(firstTrip.startDate).localeCompare(
        String(secondTrip.startDate)
      )
    )

  return {
    program,
    terms,
    omittedTripIds,
    errors: result.errors,
  }
}
